// src/theme.js
import { createTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

export function createAppTheme(mode = "dark") {
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode: isDark ? "dark" : "light",
      primary: {
        main: "#26134A",
      },
      secondary: {
        main: "#ff4081",
      },
      success: {
        main: "#56C372",
      },
      background: {
        default: isDark ? "#131120" : "#FFFFFF",
        paper: isDark ? "#1A1730" : "#FFFFFF",
      },
      text: {
        primary: isDark ? "#E6EAF2" : "#333333",
        secondary: isDark ? "#AAB4C4" : "#757575",
      },
      accent: {
        main: "#ffc107",
      },
    },
    typography: {
      fontFamily: "Helvetica Neue, Arial, sans-serif",
      h1: {
        fontSize: "2.5rem",
        fontWeight: 700,
      },
      body1: {
        fontSize: "1rem",
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "999px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
            "&:active": {
              boxShadow: "none",
            },
            "&.Mui-focusVisible": {
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              boxShadow: "none",
            },
          },
          text: ({ theme }) => ({
            ...(theme.palette.mode === "dark"
              ? {
                  color: theme.palette.text.primary,
                }
              : {}),
          }),
          textPrimary: ({ theme }) => ({
            ...(theme.palette.mode === "dark"
              ? {
                  color: theme.palette.text.primary,
                }
              : {}),
          }),
          textSecondary: ({ theme }) => ({
            ...(theme.palette.mode === "dark"
              ? {
                  color: theme.palette.text.primary,
                }
              : {}),
          }),
          outlined: ({ theme }) => ({
            ...(theme.palette.mode === "dark"
              ? {
                  color: theme.palette.text.primary,
                  borderColor: alpha(theme.palette.text.primary, 0.28),
                  "&:hover": {
                    borderColor: alpha(theme.palette.text.primary, 0.42),
                    backgroundColor: alpha(theme.palette.text.primary, 0.05),
                  },
                }
              : {}),
          }),
          outlinedPrimary: ({ theme }) => ({
            ...(theme.palette.mode === "dark"
              ? {
                  color: theme.palette.text.primary,
                  borderColor: alpha(theme.palette.text.primary, 0.28),
                  "&:hover": {
                    borderColor: alpha(theme.palette.text.primary, 0.42),
                    backgroundColor: alpha(theme.palette.text.primary, 0.05),
                  },
                }
              : {}),
          }),
          outlinedSecondary: ({ theme }) => ({
            ...(theme.palette.mode === "dark"
              ? {
                  color: theme.palette.text.primary,
                  borderColor: alpha(theme.palette.text.primary, 0.28),
                  "&:hover": {
                    borderColor: alpha(theme.palette.text.primary, 0.42),
                    backgroundColor: alpha(theme.palette.text.primary, 0.05),
                  },
                }
              : {}),
          }),
          contained: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
            "&:active": {
              boxShadow: "none",
            },
            "&.Mui-focusVisible": {
              boxShadow: "none",
            },
          },
          containedPrimary: ({ theme }) => ({
            ...(theme.palette.mode === "dark"
              ? {
                  backgroundColor: theme.palette.success.main,
                  color: theme.palette.common.white,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.success.main, 0.88),
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }
              : {}),
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
            ...(theme.palette.mode === "dark"
              ? {
                  backgroundColor: `${theme.palette.background.paper} !important`,
                  backgroundImage: "none !important",
                  color: theme.palette.text.primary,
                  border: "1px solid rgba(174,194,224,0.22)",
                }
              : {}),
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
          }),
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 44,
          }),
          indicator: ({ theme }) => ({
            height: 3,
            borderRadius: 999,
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.success.main : theme.palette.primary.main,
          }),
          scrollButtons: ({ theme }) => ({
            color: theme.palette.text.secondary,
            "&.Mui-disabled": {
              opacity: 0.35,
            },
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 44,
            minWidth: 0,
            padding: "10px 12px",
            fontSize: "0.8rem",
            fontWeight: 700,
            color: theme.palette.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            "&.Mui-selected": {
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.text.primary
                  : theme.palette.primary.main,
            },
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
          }),
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: isDark
            ? {
                "&.Mui-checked": {
                  color: "#56C372",
                },
                "&.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#56C372",
                  opacity: 1,
                },
              }
            : undefined,
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: isDark
            ? {
                "&.Mui-checked": {
                  color: "#56C372",
                },
              }
            : undefined,
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": {
              backgroundColor: theme.colors.blue,
              color: theme.palette.common.white,
            },
            "&.MuiPickersDay-today": {
              borderColor: alpha(theme.colors.blue, 0.8),
            },
          }),
        },
      },
      MuiClockNumber: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-selected": {
              backgroundColor: theme.colors.blue,
              color: theme.palette.common.white,
            },
          }),
        },
      },
      MuiClockPointer: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.colors.blue,
          }),
          thumb: ({ theme }) => ({
            borderColor: theme.colors.blue,
            backgroundColor: theme.colors.blue,
          }),
        },
      },
      MuiPickersToolbarText: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-selected": {
              color: theme.colors.blue,
            },
          }),
        },
      },
      MuiDateTimePickerToolbar: {
        styleOverrides: {
          dateContainer: ({ theme }) => ({
            "& .MuiPickersToolbarText-root": {
              fontSize: "1.9rem",
              lineHeight: 1.1,
            },
          }),
          timeContainer: ({ theme }) => ({
            alignItems: "center",
          }),
          timeDigitsContainer: ({ theme }) => ({
            alignItems: "center",
            "& .MuiPickersToolbarText-root": {
              color: theme.colors.blue,
              fontSize: "2rem",
              lineHeight: 1,
            },
          }),
          separator: ({ theme }) => ({
            color: theme.colors.blue,
            fontSize: "2rem",
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transform: "translateY(1px)",
          }),
        },
      },
      MuiTimePickerToolbar: {
        styleOverrides: {
          hourMinuteLabel: ({ theme }) => ({
            "& .MuiPickersToolbarText-root": {
              color: theme.colors.blue,
              fontSize: "2rem",
            },
          }),
          separator: ({ theme }) => ({
            color: theme.colors.blue,
            fontSize: "2rem",
          }),
        },
      },
      MuiCssBaseline: {
        styleOverrides: (theme) => ({
          ".MuiPickersLayout-root .MuiPickersToolbarButton-root.Mui-selected, .MuiPickersLayout-root .MuiTimePickerToolbar-ampmSelection .MuiButtonBase-root.Mui-selected":
            {
              backgroundColor: alpha(
                theme.colors.blue,
                theme.palette.mode === "dark" ? 0.22 : 0.14
              ),
              color: theme.colors.blue,
            },
          ".MuiPickersLayout-root .MuiTimePickerToolbar-ampmLabel.Mui-selected": {
            color: theme.colors.blue,
          },
          ".MuiPickersLayout-root .MuiClock-pin": {
            backgroundColor: theme.colors.blue,
          },
        }),
      },
    },
    colors: {
      inssa: "#26134A",
      white: "#FFFFFF",
      amber: "#EAB016",
      red: "#FF5C5C",
      green: "#56C372",
      blue: "#1152E4",
      darkBlue: "#1A0B27",
      mediumBlue: "#1A185D",
      lightGray: "#DBDBDB",
      darkGray: "#868A9C",
      headerPurple: "#20122C",
      darkPurple: "#20112D",
      lightPurple: "#8A56A5",
      mediumPurple: "#2A1E38",
      unselectedTab: "#39314A",
      lineColor: "#707070",
      paleBlue: "#4164D8",
      palePurple: "#3A324A",
      navGray: isDark ? "#1B2331" : "#F8F5F2",
      topAppBarBg: isDark ? "#0F0D1B" : "#26134A",
      bottomNavBg: isDark ? "#0F0D1B" : "#F8F5F2",
      mapBackground: isDark ? "#131120" : "#F5F7FB",
    },
  });
}

const theme = createAppTheme("dark");
export default theme;
